import { AxiosRequestConfig } from 'axios';
import useRequest from '../api/useRequest';
import useRefMounted from '../useRefMounted';
import { useState, useCallback, useEffect, useRef } from 'react';
import { clearCacheForUrl } from 'src/utils/cache/idbUtility';

interface SubmitOptions {
  setErrors?: (errors: any) => void;
  setStatus?: (status: any) => void;
  setSubmitting?: (isSubmitting: boolean) => void;
}

const DEFAULT_SUBMIT_OPTIONS: SubmitOptions = {
  setErrors: () => {},
  setStatus: () => {},
  setSubmitting: () => {},
};

// Updated useSubmit with config support and deprecation warning
const useSubmit = <D = any, R = any>(
  buildCurrentRequest: (v: D) => AxiosRequestConfig<any> | AxiosRequestConfig<any>[],
  config: { sequential?: boolean; logLevel?: string[] } | boolean = {}, // Accepts object or boolean
): [
  results: R,
  submit: (
    values: D,
    {
      setErrors = () => {},
      setStatus = () => {},
      setSubmitting = () => {},
    }: SubmitOptions,
  ) => void,
] => {
  const isMountedRef = useRefMounted();

  // Handle legacy boolean format
  if (typeof config === 'boolean') {
    console.warn(' [DEPRECATED] Passing a boolean for sequential is deprecated. Use a config object instead.');
    config = { sequential: config, logLevel: ['error', 'success', 'request'] };
  }

  // Extract config with defaults
  const { sequential = false, logLevel = ['error', 'success', 'request'] } = config;

  // Pass logLevel to useRequest
  const [results, setRequest, _abort, _loading, status] = useRequest<D, R>({ logLevel });

  const [requestQueue, setRequestQueue] = useState<AxiosRequestConfig<any>[]>([]);
  const [currentRequestIndex, setCurrentRequestIndex] = useState(0);

  // Prevent double-advancing and aborting in-flight requests
  const lastHandledIndexRef = useRef(-1);

  const processNextRequest = useCallback(() => {
    setCurrentRequestIndex((index) => {
      if (index < requestQueue.length) {
        setRequest(requestQueue[index]);
        return index + 1;
      }
      return index;
    });
  }, [requestQueue, setRequest]);

  // Kick off the first request when a new queue is loaded
  useEffect(() => {
    if (!sequential) return;
    if (requestQueue.length === 0) return;
    if (currentRequestIndex !== 0) return;
    processNextRequest();
  }, [sequential, requestQueue, currentRequestIndex, processNextRequest]);

  // Advance the queue exactly once per completed request.
  // Use status so 204/NoContent (or any falsy data) still advances.
  useEffect(() => {
    if (!sequential) return;
    if (requestQueue.length === 0) return;

    // nothing has been started yet
    if (currentRequestIndex === 0) return;

    // only advance when current request is done (not loading/idle)
    if (status === 'loading' || status === 'idle') return;

    const finishedIndex = currentRequestIndex - 1;

    // guard: handle each completion once (status may change multiple times)
    if (finishedIndex <= lastHandledIndexRef.current) return;
    lastHandledIndexRef.current = finishedIndex;

    const justFinished = requestQueue[finishedIndex];
    if (justFinished?.url) clearCacheForUrl(justFinished.url);

    processNextRequest();
  }, [sequential, status, currentRequestIndex, processNextRequest, requestQueue]);

  const submit = (
    values: D,
    { setErrors, setStatus, setSubmitting }: SubmitOptions = DEFAULT_SUBMIT_OPTIONS,
  ) => {
    try {
      const requests = buildCurrentRequest(values);

      if (sequential) {
        lastHandledIndexRef.current = -1;
        setRequestQueue(Array.isArray(requests) ? requests : [requests]);
        setCurrentRequestIndex(0);
      } else {
        if (Array.isArray(requests)) {
          requests.forEach((request) => setRequest(request));
        } else {
          setRequest(requests);
        }
      }

      // Keep your current behavior: mark as submitted immediately
      // (Formik isSubmitting will flip off immediately as before)
      if (isMountedRef()) {
        setStatus({ success: true });
        setSubmitting(false);
      }
    } catch (err: any) {
      console.error(err);
      if (isMountedRef()) {
        setStatus({ success: false });
        setErrors({ submit: err.message });
        setSubmitting(false);
      }
    }
  };

  return [results, submit];
};

export default useSubmit;